import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
const SWIPE_CLOSE_DISTANCE = 80;
const SWIPE_NEXT_DISTANCE = 50;
const FLICK_MIN_DISTANCE = 30;
const FLICK_MIN_SPEED = 0.5;
const FLICK_MAX_MS = 400;
// The photo opens at 1.5x; anything beyond that was pinched in by the shopper.
const SWIPE_MAX_SCALE = 1.6;

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {{x: number, y: number, t: number} | null} */
  #swipe = null;
  #closing = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    // Passive on purpose: the image's own drag handler already owns preventDefault.
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { passive: true });
    this.refs.dialog.addEventListener('touchmove', this.#handleSwipeMove, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { passive: true });
    this.refs.dialog.addEventListener('touchcancel', this.#handleSwipeCancel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSwipeStart);
    this.refs.dialog.removeEventListener('touchmove', this.#handleSwipeMove);
    this.refs.dialog.removeEventListener('touchend', this.#handleSwipeEnd);
    this.refs.dialog.removeEventListener('touchcancel', this.#handleSwipeCancel);
  }

  /**
   * Key that identifies a photo whichever list it sits in: the file name of its first image,
   * without size parameters. The zoom list and the page gallery render the same media, but the page
   * hides the photos of other carats and metals, so they are matched by file rather than by position.
   * @param {Element | null | undefined} element
   * @returns {string}
   */
  #photoKey(element) {
    const image = element?.querySelector('img');
    const source = image?.getAttribute('src') || image?.currentSrc || '';
    if (!source) return '';
    try {
      return new URL(source, window.location.href).pathname;
    } catch {
      return source.split('?')[0] ?? '';
    }
  }

  /**
   * The photos the page shows right now, in the order it shows them: the desktop grid, or the phone
   * slideshow, whichever is on screen, minus anything the carat and metal filter has hidden.
   * @returns {HTMLElement[]}
   */
  #pageItems() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return [];
    const grid = gallery.querySelector('.media-gallery__grid');
    const gridShown = grid instanceof HTMLElement && grid.getClientRects().length > 0;
    const items = gridShown ? Array.from(grid.children) : Array.from(gallery.querySelectorAll('slideshow-slide'));
    return /** @type {HTMLElement[]} */ (
      items.filter((item) => item instanceof HTMLElement && getComputedStyle(item).display !== 'none')
    );
  }

  /**
   * Makes the zoom list hold exactly the photos the page shows for the selected carat and metal, in
   * the page's order. Runs each time the dialog opens, so it follows every carat or metal change.
   * Photos the page hides stay in the markup, hidden, and are put back in order on the next run.
   */
  #syncToPage() {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;

    // Remember where each photo started so a tap on the page can find its photo after a reorder.
    media.forEach((item, index) => {
      if (item.dataset.originIndex === undefined) item.dataset.originIndex = String(index);
    });
    const thumbs = /** @type {HTMLElement[]} */ (Array.from(thumbnails?.children ?? []));
    thumbs.forEach((thumb, index) => {
      if (thumb.dataset.originIndex === undefined) thumb.dataset.originIndex = String(index);
    });

    const wanted = [];
    for (const item of this.#pageItems()) {
      const key = this.#photoKey(item);
      if (key && !wanted.includes(key)) wanted.push(key);
    }
    const byKey = new Map();
    for (const item of media) {
      const key = this.#photoKey(item);
      if (key && !byKey.has(key)) byKey.set(key, item);
    }
    const shown = wanted.map((key) => byKey.get(key)).filter(Boolean);
    // Nothing matched (page filter not applied yet, or unfamiliar markup): leave the list as it is.
    if (!shown.length) return;

    const origin = (/** @type {HTMLElement} */ el) => Number(el.dataset.originIndex);
    const hidden = media.filter((item) => !shown.includes(item)).sort((a, b) => origin(a) - origin(b));
    const list = shown[0]?.parentElement;
    for (const item of [...shown, ...hidden]) {
      item.style.display = shown.includes(item) ? '' : 'none';
      list?.appendChild(item);
    }

    if (thumbs.length) {
      const thumbFor = (/** @type {HTMLElement} */ item) => thumbs.find((t) => t.dataset.originIndex === item.dataset.originIndex);
      const shownThumbs = shown.map(thumbFor).filter(Boolean);
      const hiddenThumbs = hidden.map(thumbFor).filter(Boolean);
      for (const thumb of [...shownThumbs, ...hiddenThumbs]) {
        thumb.style.display = shownThumbs.includes(thumb) ? '' : 'none';
        thumb.parentElement?.appendChild(thumb);
      }
    }

    // The component keeps its refs in DOM order, so refresh them after the move.
    this.updatedCallback();
  }

  /**
   * The page photo that matches a zoom photo, so closing lands on the right picture.
   * @param {HTMLElement} zoomItem
   * @param {HTMLElement[]} pageList
   * @returns {HTMLElement | undefined}
   */
  #pageMatch(zoomItem, pageList) {
    const key = this.#photoKey(zoomItem);
    return key ? pageList.find((item) => this.#photoKey(item) === key) : undefined;
  }

  /** @param {TouchEvent} event */
  #handleSwipeStart = (event) => {
    this.#swipe = null;
    if (event.touches.length !== 1) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.dialog-thumbnails-list-container, .dialog-zoomed-gallery__close-button')) return;
    const touch = event.touches[0];
    if (!touch) return;
    this.#swipe = { x: touch.clientX, y: touch.clientY, t: performance.now() };
  };

  /** @param {TouchEvent} event */
  #handleSwipeMove = (event) => {
    // A second finger means a pinch: not a swipe.
    if (event.touches.length > 1) this.#swipe = null;
  };

  #handleSwipeCancel = () => {
    this.#swipe = null;
  };

  /** @param {TouchEvent} event */
  #handleSwipeEnd = (event) => {
    const start = this.#swipe;
    this.#swipe = null;
    if (!start || event.touches.length !== 0) return;
    const touch = event.changedTouches[0];
    if (!touch) return;

    // A photo the shopper pinched in on is for panning; swipes only act on the opening view.
    const wrapper = event.target instanceof Element ? event.target.closest('drag-zoom-wrapper') : null;
    const scale = wrapper instanceof HTMLElement ? parseFloat(wrapper.style.getPropertyValue('--drag-zoom-scale')) : 1;
    if (scale > SWIPE_MAX_SCALE) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = Math.max(performance.now() - start.t, 1);
    const flick = (distance) => elapsed <= FLICK_MAX_MS && Math.abs(distance) / elapsed >= FLICK_MIN_SPEED;

    if (dy > 0 && dy > Math.abs(dx) * 1.2 && (dy >= SWIPE_CLOSE_DISTANCE || (dy >= FLICK_MIN_DISTANCE && flick(dy)))) {
      this.close();
      return;
    }

    if (
      window.matchMedia('(max-width: 749px)').matches &&
      Math.abs(dx) > Math.abs(dy) * 1.2 &&
      (Math.abs(dx) >= SWIPE_NEXT_DISTANCE || (Math.abs(dx) >= FLICK_MIN_DISTANCE && flick(dx)))
    ) {
      this.#step(dx < 0 ? 1 : -1);
    }
  };

  /**
   * Moves to the next or previous photo in the list.
   * @param {number} direction - 1 for next, -1 for previous.
   */
  #step(direction) {
    const { dialog, media } = this.refs;
    const shown = media.filter((item) => item.style.display !== 'none');
    const list = shown[0]?.parentElement;
    if (!list || !shown.length) return;
    const current = Math.round(list.scrollLeft / Math.max(list.clientWidth, 1));
    const target = shown[Math.min(Math.max(current + direction, 0), shown.length - 1)];
    if (!target || !dialog.open) return;
    this.selectThumbnail(media.indexOf(target), { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    // Show exactly the photos the page shows for the selected carat and metal, then find the one tapped.
    this.#syncToPage();

    const { dialog, media, thumbnails } = this.refs;
    const tapped = media.find((item) => item.dataset.originIndex === String(index)) ?? media[index];
    const targetImage = tapped && tapped.style.display !== 'none' ? tapped : media.find((item) => item.style.display !== 'none');
    const targetThumbnail = thumbnails.children[media.indexOf(/** @type {HTMLElement} */ (targetImage))];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(media.indexOf(targetImage), { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#pageIndex(mostVisibleElement, activeIndex)));
  }, 50);

  /**
   * Position of a zoom photo in the page's slideshow, so the page follows the zoom view.
   * @param {HTMLElement | undefined} zoomItem
   * @param {number} fallback
   * @returns {number}
   */
  #pageIndex(zoomItem, fallback) {
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('media-gallery'));
    const slides = gallery?.slideshow?.slides ?? [];
    const match = zoomItem ? this.#pageMatch(zoomItem, slides) : undefined;
    if (match) return slides.indexOf(match);
    return zoomItem?.dataset.originIndex !== undefined ? Number(zoomItem.dataset.originIndex) : fallback;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    if (this.#closing) return;
    this.#closing = true;
    try {
      await this.#closeWithTransition();
    } finally {
      this.#closing = false;
    }
  }

  async #closeWithTransition() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const pageList = (slideshowActive ? mediaGallery.slideshow?.slides : mediaGallery?.media) ?? [];
    const slide =
      this.#pageMatch(mostVisibleElement, pageList) ??
      pageList[Number(mostVisibleElement.dataset.originIndex ?? activeIndex)];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#pageIndex(targetImage, index)));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
